import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { Provider } from 'src/typings';
import { ProviderCreateDialogComponent } from '../create-dialog/create-dialog.component';
import { ProviderService } from '../provider.service';

type ProviderFilters = { description?: string; taxNum?: string; orgNum?: string };

@Component({ selector: 'app-list', templateUrl: './list.component.html', styleUrls: ['./list.component.scss'] })
export class ListComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private providerService: ProviderService, private fb: UntypedFormBuilder,
    private dialog: MatDialog, private router: Router, private route: ActivatedRoute) {}

  @ViewChild('listViewport') listViewport?: ElementRef<HTMLElement>;
  providers: Provider[] = [];
  displayedColumns = ['id', 'description', 'fullDescription', 'taxNum', 'orgNum', 'warehouses', 'action'];
  filtersForm = this.fb.group({ description: [''], taxNum: [''], orgNum: [''] });
  filtersOpened = typeof window === 'undefined' || window.innerWidth > 900;
  appliedFilters: ProviderFilters = {};
  loading = false;
  error = '';
  resultsLength = 0;
  pageSize = 30;
  sort_active = 'description';
  sort_direction: 'asc' | 'desc' = 'asc';
  creating = false;
  private offset = 0;
  private revision = 0;
  private destroyed = false;
  private destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  private frame: number | null = null;
  private readonly sortColumns = ['id', 'description', 'fullDescription', 'taxNum', 'orgNum'];

  get hasMore() { return this.offset < this.resultsLength; }
  get activeFiltersCount() { return Object.keys(this.appliedFilters).length; }
  get profileQueryParams() {
    return { returnDescription: this.appliedFilters.description, returnTaxNum: this.appliedFilters.taxNum,
      returnOrgNum: this.appliedFilters.orgNum, returnSort: this.sort_active, returnDirection: this.sort_direction };
  }

  ngOnInit() {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.appliedFilters = this.normalizeFilters({ description: params.get('description'), taxNum: params.get('taxNum'), orgNum: params.get('orgNum') });
      const sort = params.get('sort') || '';
      this.sort_active = this.sortColumns.includes(sort) ? sort : 'description';
      this.sort_direction = params.get('direction') === 'desc' ? 'desc' : 'asc';
      this.filtersForm.reset({ description: this.appliedFilters.description || '', taxNum: this.appliedFilters.taxNum || '', orgNum: this.appliedFilters.orgNum || '' }, { emitEvent: false });
      this.reload();
    });
  }

  ngAfterViewInit() {
    if (typeof ResizeObserver !== 'undefined' && this.listViewport) {
      this.resizeObserver = new ResizeObserver(() => this.checkEdge());
      this.resizeObserver.observe(this.listViewport.nativeElement);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.revision++;
    this.destroy$.next(); this.destroy$.complete();
    this.resizeObserver?.disconnect();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  search() { this.applyQuery(this.normalizeFilters(this.filtersForm.value)); }
  resetFilters() {
    this.filtersForm.reset({ description: '', taxNum: '', orgNum: '' });
    this.applyQuery({});
  }
  sortData(event: Sort) {
    if (!this.sortColumns.includes(event.active)) return;
    this.applyQuery(this.appliedFilters, event.active, event.direction || 'asc');
  }
  private applyQuery(filters: ProviderFilters, sort = this.sort_active, direction = this.sort_direction) {
    if (JSON.stringify(filters) === JSON.stringify(this.appliedFilters) && sort === this.sort_active && direction === this.sort_direction) this.reload();
    else void this.router.navigate([], { relativeTo: this.route, queryParams: { ...filters, sort, direction } });
  }

  reload() {
    this.revision++;
    this.providers = []; this.resultsLength = 0; this.offset = 0; this.loading = false; this.error = '';
    if (this.listViewport) this.listViewport.nativeElement.scrollTop = 0;
    void this.loadMore();
  }
  async loadMore() {
    if (this.destroyed || this.loading || (this.offset > 0 && !this.hasMore)) return;
    const revision = this.revision;
    this.loading = true; this.error = '';
    try {
      const result = await firstValueFrom(this.providerService.search({ ...this.appliedFilters,
        limit: this.pageSize, offset: this.offset, order_active: this.sort_active, order_direction: this.sort_direction }).pipe(takeUntil(this.destroy$)));
      if (this.destroyed || revision !== this.revision) return;
      const known = new Set(this.providers.map(provider => provider.id));
      const next = result.data.filter(provider => { if (known.has(provider.id)) return false; known.add(provider.id); return true; });
      this.providers = [...this.providers, ...next];
      this.offset += result.data.length;
      this.resultsLength = Number(result.count) || 0;
      if (!result.data.length) this.offset = this.resultsLength;
    } catch {
      if (!this.destroyed && revision === this.revision) this.error = 'Не удалось загрузить поставщиков. Попробуйте ещё раз.';
    } finally {
      if (!this.destroyed && revision === this.revision) {
        this.loading = false;
        if (typeof requestAnimationFrame !== 'undefined') {
          if (this.frame !== null) cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => { this.frame = null; this.checkEdge(); });
        }
      }
    }
  }

  @HostListener('window:resize')
  checkEdge() {
    const viewport = this.listViewport?.nativeElement;
    if (viewport && viewport.clientHeight > 0 && !this.loading && !this.error && this.hasMore &&
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 160) void this.loadMore();
  }

  createProvider() {
    if (this.creating) return;
    this.creating = true;
    this.dialog.open(ProviderCreateDialogComponent, {
      width: '560px', maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 24px)',
      data: { description: this.normalizeFilters(this.filtersForm.value).description }
    }).afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(provider => {
      this.creating = false;
      if (provider?.id) void this.router.navigate(['/provider/profile', provider.id], { queryParams: this.profileQueryParams });
    });
  }

  getWarehouseSummary(provider: Provider) {
    const warehouses = provider.providerWarehouse || [];
    if (!warehouses.length) return 'Нет складов';
    return warehouses.map(warehouse => [warehouse.description, warehouse.city?.description].filter(Boolean).join(', ')).join(' / ');
  }
  trackProvider(_: number, provider: Provider) { return provider.id; }
  private normalizeFilters(value: any): ProviderFilters {
    const result: ProviderFilters = {};
    for (const key of ['description', 'taxNum', 'orgNum'] as const) {
      const text = String(value[key] || '').trim();
      if (text) result[key] = text;
    }
    return result;
  }
}
